// Số liệu Báo cáo CCO theo tuần.
//  • Telesales: tính từ cùng nguồn với Dashboard Telesales (buildModel + logic) → hai màn khớp số.
//  • CSKH, báo giá, kênh nhắn tin, QA, mức trợ giá: số minh hoạ cố định theo tuần — thay bằng API khi có.
import dayjs from 'dayjs';
import type { Mang } from '../Telesales/components/Types';
import { AGENTS, agentOf, rng, type Call, type Model } from '../Telesales/dashboard/data';
import { ARRIVED, CLOSED, agentPerf, apptStatus, breakdown, callStats, callsIn, firstCallMin, funnel, pct, tasks, type Ctx, type Range } from '../Telesales/dashboard/logic';

/* ---------- kỳ báo cáo: tuần thứ 2 → chủ nhật ---------- */
export interface Week { key: string; from: number; to: number; end: number; label: string; short: string; running: boolean }

/** Số tuần ISO-8601 */
export const isoWeek = (t: number) => {
  const d = dayjs(t).startOf('day');
  const thu = d.add(3 - ((d.day() + 6) % 7), 'day');
  return Math.floor(thu.diff(thu.startOf('year'), 'day') / 7) + 1;
};

/** Tuần lùi `back` tuần so với tuần chứa `now` (0 = tuần này, số tới thời điểm hiện tại) */
export function weekOf(now: number, back: number): Week {
  const d = dayjs(now).startOf('day');
  const mon = d.subtract((d.day() + 6) % 7, 'day').subtract(back * 7, 'day');
  const end = mon.add(7, 'day').valueOf() - 1;
  const n = isoWeek(mon.valueOf());
  return {
    key: mon.format('YYYY-MM-DD'), from: mon.valueOf(), to: Math.min(end, now), end, running: end > now,
    label: `Tuần ${n} (${mon.format('DD/MM')}–${dayjs(end).format('DD/MM')})`, short: `T${n}`,
  };
}
/** Số tuần được chọn — dữ liệu cuộc gọi giữ 35 ngày, cần thêm 1 tuần trước để so sánh */
export const WEEK_BACK = 4;

/* ---------- Telesales ---------- */
const callMang = (cl: Call, m: Model) => (cl.leadId ? m.leadById[cl.leadId].mang : agentOf(cl.agentId)?.mang ?? undefined);
const callsOf = (c: Ctx, r: Range, mang?: Mang) => callsIn(c.m, { from: r.from, to: Math.min(r.to, c.now) }).filter(cl => !mang || callMang(cl, c.m) === mang);
/** Cuộc thực chất: gọi đi kết nối và nói quá ngưỡng (giây) */
export const QUALITY_SEC = 60;
/** Lệch mức chung từ bao nhiêu điểm thì tô màu / đưa vào điểm cần chú ý */
export const GAP = 8;

export interface TeleKpi {
  /** Phễu theo khách vào trong kỳ: mới, đã gọi, kết nối, đặt lịch, đến, chốt/cọc */
  fn: number[];
  revenue: number;
  firstMin: number | null;
  /** % khách mới (không tính Hotline) được gọi ngay trong ngày vào */
  sameDay: number;
  /** Số lần gọi đến khi kết nối được (trung bình / khách đã gọi) */
  tries: number;
  inbound: number;
  missed: number;
  sl: number;
  asa: number;
  aht: number;
  out: number;
  outOk: number;
  quality: number;
}

export function teleKpi(c: Ctx, r: Range, mang?: Mang): TeleKpi {
  const fn = funnel(c, r, { mang });
  const revenue = breakdown(c, r, 'mang', { mang }).reduce((s, x) => s + x.revenue, 0);
  const calls = callsOf(c, r, mang);
  const s = callStats(calls, c.cfg);
  const outOk = calls.filter(cl => cl.dir === 'out' && cl.ok);

  // Lần gọi đầu và số lần thử của khách vào trong kỳ
  const first: Record<string, number> = {};
  const tries: Record<string, number> = {};
  for (const cl of callsIn(c.m, { from: r.from, to: c.now })) {
    if (!cl.leadId || cl.dir !== 'out' || cl.kind !== 'first') continue;
    if (!(cl.leadId in first)) first[cl.leadId] = cl.at;
    tries[cl.leadId] = (tries[cl.leadId] ?? 0) + 1;
  }
  const fresh = c.m.leads.filter(l => l.createdAt >= r.from && l.createdAt <= Math.min(r.to, c.now) && l.source !== 'Hotline' && (!mang || l.mang === mang));
  const called = fresh.filter(l => first[l.id]);
  const sameDay = fresh.filter(l => first[l.id] && dayjs(first[l.id]).isSame(l.createdAt, 'day')).length;
  const tryTotal = called.reduce((n, l) => n + tries[l.id], 0);

  return {
    fn, revenue,
    firstMin: firstCallMin(c, r, { mang }),
    sameDay: pct(sameDay, fresh.length),
    tries: called.length ? Math.round((tryTotal / called.length) * 10) / 10 : 0,
    inbound: s.inbound, missed: s.missed, sl: pct(s.inSla, s.inbound), asa: s.asa, aht: s.aht,
    out: s.out, outOk: s.connected,
    quality: pct(outOk.filter(cl => cl.talkSec > QUALITY_SEC).length, outOk.length),
  };
}

export interface AgentRow {
  id: string;
  mang: Mang | null;
  out: number;
  connect: number;
  quality: number;
  /** Lịch đặt / khách kết nối trong kỳ */
  book: number;
  /** Khách đến / lịch đã tới giờ hẹn */
  show: number;
  /** Chốt-cọc / khách đến */
  close: number;
  booked: number;
  /** Khách kết nối trong kỳ (mẫu số đặt lịch / kết nối) */
  conn: number;
  arrived: number;
  due: number;
  closed: number;
  revenue: number;
  /** Đơn chốt theo mức trợ giá (%): không trợ giá · mức 1 · mức 2 — minh hoạ */
  subsidy: [number, number, number];
  /** Điểm QA — minh hoạ, null: chưa chấm */
  qa: number | null;
  /** Hẹn gọi lại đã quá hạn (tại thời điểm xem) */
  overdue: number;
}

const int = (r: () => number, a: number, b: number) => a + Math.floor(r() * (b - a + 1));

export function agentRows(c: Ctx, r: Range, weekKey: string, mang?: Mang): AgentRow[] {
  const perf = agentPerf(c, r, { mang });
  const calls = callsOf(c, r, mang);
  const overdue = tasks(c, { mang }).filter(t => t.kind === 'callback' && t.due < c.now);
  return AGENTS.filter(a => !mang || !a.mang || a.mang === mang).map(a => {
    const p = perf.find(x => x.id === a.id)!;
    const ok = calls.filter(cl => cl.agentId === a.id && cl.dir === 'out' && cl.ok);
    const connectedLeads = c.m.leads.filter(l => l.ownerId === a.id && (!mang || l.mang === mang) && l.connectAt && l.connectAt >= r.from && l.connectAt <= Math.min(r.to, c.now)).length;
    const rnd = rng(`cco-${a.id}-${weekKey}`);
    const s0 = int(rnd, 35, 60);
    const s1 = int(rnd, 20, 35);
    return {
      id: a.id, mang: a.mang, out: p.out,
      connect: pct(p.connected, p.out),
      quality: pct(ok.filter(cl => cl.talkSec > QUALITY_SEC).length, ok.length),
      book: pct(p.booked, connectedLeads),
      show: pct(p.arrived, p.due),
      close: pct(p.closed, p.arrived),
      booked: p.booked, conn: connectedLeads, arrived: p.arrived, due: p.due, closed: p.closed, revenue: p.revenue,
      subsidy: [s0, s1, 100 - s0 - s1],
      qa: a.mang ? int(rnd, 68, 88) : rnd() < 0.5 ? null : int(rnd, 64, 80),
      overdue: overdue.filter(t => t.agentId === a.id).length,
    };
  });
}

/** Mức chung cả phòng — để so từng nhân viên */
export function teamOf(rows: AgentRow[]) {
  const t = (f: (a: AgentRow) => number) => rows.reduce((s, a) => s + f(a), 0);
  return {
    out: t(a => a.out), booked: t(a => a.booked), closed: t(a => a.closed), overdue: t(a => a.overdue), revenue: t(a => a.revenue),
    book: pct(t(a => a.booked), t(a => a.conn)), show: pct(t(a => a.arrived), t(a => a.due)), close: pct(t(a => a.closed), t(a => a.arrived)),
  };
}

/**
 * Xu hướng theo tuần: đặt lịch / kết nối và chốt-cọc / đến (theo khách vào trong tuần).
 * Không dựa vào nhật ký cuộc gọi (chỉ giữ 35 ngày) để vẽ đủ các tuần cũ.
 */
export function trend(c: Ctx, weeks: Week[], mang?: Mang) {
  return weeks.map(w => {
    let conn = 0, booked = 0, arrived = 0, closed = 0;
    for (const l of c.m.leads) {
      if (l.createdAt < w.from || l.createdAt > w.to || (mang && l.mang !== mang) || !l.connectAt || l.connectAt > c.now) continue;
      conn++;
      const a = l.apptId ? c.m.apptById[l.apptId] : undefined;
      if (!a || a.bookedAt > c.now) continue;
      booked++;
      const st = apptStatus(a, c);
      if (ARRIVED.includes(st)) arrived++;
      if (CLOSED.includes(st)) closed++;
    }
    return { week: w.short, label: w.label, book: pct(booked, conn), close: pct(closed, arrived), running: w.running };
  });
}

/* ---------- CSKH (minh hoạ) ---------- */
export const CS_STAGES = [
  { code: 'CS0', label: 'Mới chuyển sang, chờ T+24h' },
  { code: 'CS1', label: 'Đang chăm sóc sau dịch vụ' },
  { code: 'CS2', label: 'Đang trong liệu trình' },
  { code: 'CS3', label: 'Hoàn tất liệu trình, ổn định' },
  { code: 'CS4', label: 'Đã đặt lịch dùng tiếp / mua thêm' },
  { code: 'CS5', label: 'Lâu chưa quay lại' },
  { code: 'CS6', label: 'Ngừng chăm sóc' },
] as const;
const CS_BASE = [6, 18, 142, 260, 35, 410, 52];

export interface CsKpi {
  t24: number; csat: number; csatN: number; csatLow: number;
  complaints: number; complaintsOnTime: number;
  returnRate: number; revenue: number; recallAC: number;
  photo: number; cs5Back: number; cs5: number;
  dist: number[];
  staff: { id: string; calls: number; t24: number; csat: number; done: number; total: number; orders: number; revenue: number }[];
  recall: { dept: 'CSKH' | 'Telesales'; group: string; desc: string; channel: string; touched: number; booked: number }[];
}
/** Chuẩn SOP gọi lại khách cũ nhóm A, C mỗi tuần */
export const RECALL_SOP = 30;

export function csKpi(weekKey: string): CsKpi {
  const r = rng(`cco-cs-${weekKey}`);
  const dist = CS_BASE.map(n => Math.max(1, n + Math.round((r() - 0.5) * n * 0.16)));
  const complaints = int(r, 2, 5);
  const staff = [
    { id: 'NV-07', calls: int(r, 80, 105), t24: int(r, 90, 100), csat: 4.3 + int(r, 0, 4) / 10, total: int(r, 1, 3), orders: int(r, 3, 6), revenue: int(r, 100, 150) * 1e6 },
    { id: 'NV-08', calls: int(r, 50, 75), t24: int(r, 82, 96), csat: 4 + int(r, 0, 4) / 10, total: int(r, 1, 3), orders: int(r, 1, 4), revenue: int(r, 40, 80) * 1e6 },
  ].map(s => ({ ...s, done: Math.max(0, s.total - int(r, 0, 1)) }));
  const recall: CsKpi['recall'] = [
    { dept: 'CSKH', group: 'A', desc: 'Đã dùng dịch vụ, dừng > 60 ngày', channel: 'Gọi', touched: int(r, 6, 20), booked: int(r, 1, 4) },
    { dept: 'CSKH', group: 'C', desc: 'Đã dùng dịch vụ da/tiêm, chưa có thẻ thành viên', channel: 'Zalo', touched: int(r, 3, 12), booked: int(r, 0, 2) },
    { dept: 'Telesales', group: 'B', desc: '“Cần suy nghĩ” (KN-03), chưa dùng dịch vụ', channel: 'Zalo + Gọi', touched: int(r, 8, 16), booked: int(r, 1, 4) },
    { dept: 'Telesales', group: 'D', desc: 'Đã hỏi, chưa đặt lịch / rót chưa đặt lịch', channel: 'Zalo', touched: int(r, 2, 8), booked: int(r, 0, 2) },
  ];
  const cs5 = dist[5];
  return {
    t24: int(r, 86, 100), csat: 4.1 + int(r, 0, 6) / 10, csatN: int(r, 28, 40), csatLow: int(r, 1, 4),
    complaints, complaintsOnTime: complaints - int(r, 0, 1),
    returnRate: int(r, 34, 42), revenue: int(r, 150, 220) * 1e6,
    recallAC: recall.filter(x => x.dept === 'CSKH').reduce((s, x) => s + x.touched, 0),
    photo: int(r, 64, 82), cs5, cs5Back: Math.round(cs5 * int(r, 3, 6) / 100),
    dist, staff, recall,
  };
}

/* ---------- Báo giá, kênh nhắn tin, xin duyệt vượt quyền (minh hoạ, trừ dòng Gọi điện) ---------- */
export interface QuoteKpi {
  quotes: number; quoteBooked: number; expired: number; priceGap: number; gapOrders: number;
  channels: { channel: string; touches: number; reply: number; booked: number; replyMin: number | null; real?: boolean }[];
  overrides: { id: string; quotes: number; requested: number; approved: number }[];
}
export function quoteKpi(c: Ctx, w: Week, tk: TeleKpi, mang?: Mang): QuoteKpi {
  const r = rng(`cco-q-${w.key}-${mang ?? 'all'}`);
  const k = mang ? 0.35 : 1;
  const quotes = Math.round(int(r, 100, 135) * k);
  const zalo = Math.round(int(r, 360, 450) * k);
  const wa = Math.round(int(r, 15, 30) * k);
  return {
    quotes, quoteBooked: Math.round(quotes * int(r, 28, 40) / 100),
    expired: Math.round(int(r, 12, 24) * k), priceGap: int(r, 16, 26), gapOrders: Math.round(int(r, 30, 45) * k),
    channels: [
      { channel: 'Gọi điện', touches: tk.out, reply: pct(tk.outOk, tk.out), booked: tk.fn[3], replyMin: null, real: true },
      { channel: 'Zalo', touches: zalo, reply: int(r, 42, 52), booked: Math.round(zalo * int(r, 6, 8) / 100), replyMin: int(r, 3, 7) },
      { channel: 'WhatsApp', touches: wa, reply: int(r, 45, 60), booked: int(r, 1, 4), replyMin: int(r, 4, 9) },
    ],
    overrides: AGENTS.filter(a => !mang || !a.mang || a.mang === mang).map(a => {
      const q = int(r, 12, 28);
      const req = int(r, 0, 6);
      return { id: a.id, quotes: q, requested: req, approved: req ? int(r, Math.ceil(req / 3), req) : 0 };
    }),
  };
}

/* ---------- điểm cần chú ý ---------- */
export type Level = 'red' | 'amber' | 'info';
export interface Note { level: Level; text: string; section: 'tele' | 'cs' | 'quote' }

export function notes(o: { cfg: Ctx['cfg']; tk: TeleKpi; agents: AgentRow[]; cs: CsKpi; checking: number; mang?: Mang }): Note[] {
  const { cfg, tk, agents, cs } = o;
  const out: Note[] = [];
  if (tk.firstMin != null && tk.firstMin > cfg.newLeadMin) out.push({ level: 'red', section: 'tele', text: `Tốc độ gọi khách mới ${tk.firstMin} phút — chậm hơn chuẩn ${cfg.newLeadMin} phút` });
  if (tk.inbound && tk.sl < 80) out.push({ level: 'red', section: 'tele', text: `Service level ${tk.sl}% — dưới 80% (nhận máy trong ${cfg.slaSec} giây)` });
  if (tk.inbound && pct(tk.missed, tk.inbound) > 10) out.push({ level: 'amber', section: 'tele', text: `${pct(tk.missed, tk.inbound)}% cuộc gọi đến bị nhỡ (${tk.missed} cuộc)` });
  const team = teamOf(agents).book;
  agents.filter(a => a.conn >= 5 && team - a.book >= GAP)
    .forEach(a => out.push({ level: 'amber', section: 'tele', text: `${a.id}: đặt lịch / kết nối ${a.book}% — thấp hơn mức chung ${team - a.book} điểm` }));
  const overdue = agents.reduce((s, a) => s + a.overdue, 0);
  if (overdue) out.push({ level: 'amber', section: 'tele', text: `${overdue} hẹn gọi lại đã quá hạn (${agents.filter(a => a.overdue).map(a => a.id).join(', ')})` });
  const unassigned = agents.filter(a => !a.mang);
  if (unassigned.length && !o.mang) out.push({ level: 'info', section: 'tele', text: `${unassigned.length} nhân viên chưa phân luồng mảng (${unassigned.map(a => a.id).join(', ')})` });
  if (cs.t24 < 100) out.push({ level: cs.t24 < 90 ? 'red' : 'amber', section: 'cs', text: `CSKH gọi T+24h đúng hạn ${cs.t24}% — chuẩn SOP 100%` });
  if (cs.complaintsOnTime < cs.complaints) out.push({ level: 'red', section: 'cs', text: `${cs.complaints - cs.complaintsOnTime} khiếu nại xử lý trễ hạn` });
  if (cs.recallAC < RECALL_SOP) out.push({ level: 'amber', section: 'cs', text: `Gọi lại khách cũ nhóm A, C: ${cs.recallAC} lượt — chuẩn SOP ≥ ${RECALL_SOP} / tuần` });
  if (o.checking) out.push({ level: 'info', section: 'quote', text: `${o.checking} khoản cọc online đang chờ Kế toán đối soát` });
  const rank: Record<Level, number> = { red: 0, amber: 1, info: 2 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}

/** Các điểm chưa chốt — đang dùng giá trị tạm, số liệu liên quan chưa chính thức */
export const PENDING: { item: string; now: string; affects: string }[] = [
  { item: 'SLA gọi khách mới', now: 'Theo ngưỡng Dashboard Telesales', affects: 'Tốc độ gọi khách mới' },
  { item: 'Ngưỡng cuộc thực chất', now: `> ${QUALITY_SEC} giây đàm thoại`, affects: '% cuộc thực chất' },
  { item: 'Khung mức trợ giá', now: 'Không trợ giá · Mức 1 · Mức 2 (minh hoạ)', affects: 'Đơn chốt theo mức trợ giá, xin duyệt vượt quyền' },
  { item: 'Phiếu chấm QA', now: 'Chưa có — điểm minh hoạ', affects: 'Điểm QA' },
  { item: 'Ánh xạ nhóm ngân sách → mảng', now: 'Chưa ánh xạ — lọc theo mảng', affects: 'Bộ lọc nhóm ngân sách' },
  { item: 'Ngưỡng CS5 (lâu chưa quay lại)', now: 'Minh hoạ', affects: 'Phân bố CS0–CS6, CS5 → quay lại' },
  { item: 'Hạn xử lý khiếu nại', now: 'Minh hoạ', affects: 'Khiếu nại đúng hạn' },
  { item: 'Liên kết CMS ↔ POS', now: 'Doanh số lấy theo kết quả tư vấn', affects: 'Doanh thu phễu Telesales' },
];
